/**
 * Intelligence analysis types — focal points, surge events, convergence cells,
 * CII scores, correlation signals, and combined analysis results.
 */

import type { NormalizedEvent, Severity } from '@/sources/base-source';

/** Get event timestamp in ms, using pre-computed value when available. */
export function getEventTimestamp(event: NormalizedEvent): number {
  return event._timestampMs ?? new Date(event.timestamp).getTime();
}

/** A signal detected by analysis — an individual data point contributing to a focal point. */
export interface Signal {
  type: string;
  description: string;
  severity: Severity;
  timestamp: string;
}

/** Focal point — an entity receiving multi-source intelligence attention. */
export interface FocalPoint {
  id: string;
  entity: string;
  urgencyLevel: 'critical' | 'elevated' | 'watch';
  score: number;
  newsScore: number;
  signalScore: number;
  correlationBonus: number;
  mentionCount: number;
  signals: Signal[];
  narrative: string;
  detectedAt: string;
}

/** Surge event — abnormal volume spike for a source type. */
export interface SurgeEvent {
  sourceType: string;
  count: number;
  baseline: number;
  ratio: number;
  region: string;
}

/** Convergence cell — geographic grid cell with multiple event types. */
export interface ConvergenceCell {
  lat: number;
  lng: number;
  eventTypes: string[];
  totalEvents: number;
  score: number;
  label: string;
}

/** Country Instability Index score. */
export interface CIIScore {
  countryCode: string;
  countryName: string;
  overall: number;
  components: {
    unrest: number;
    conflict: number;
    security: number;
    information: number;
  };
  trend: 'rising' | 'falling' | 'stable';
  lastUpdated: string;
}

/** Cross-source correlation signal. */
export interface CorrelationSignal {
  id: string;
  type: 'multi-source-convergence' | 'news-data-alignment' | 'cascade-detection';
  description: string;
  confidence: number;
  sources: string[];
  detectedAt: string;
}

// ---------------------------------------------------------------------------
// Phase 2 — Intelligence Engine types
// ---------------------------------------------------------------------------

/** Hotspot escalation — tracked escalation state for a geopolitical hotspot. */
export interface HotspotEscalation {
  hotspotId: string;
  hotspotName: string;
  lat: number;
  lng: number;
  dimensions: {
    news: number;       // 0-100, weight 35%
    cii: number;        // 0-100, weight 25%
    convergence: number; // 0-100, weight 25%
    military: number;   // 0-100, weight 15%
  };
  score: number;        // Composite 0-100
  trend: 'rising' | 'stable' | 'falling';
  alertCooldown: boolean; // True if alert fired within last 2 hours
  detectedAt: string;
}

/** Infrastructure cascade path step. */
export interface CascadePathStep {
  nodeId: string;
  nodeName: string;
  depth: number;
  impact: number;
}

/** Infrastructure cascade result — BFS propagation through dependency graph. */
export interface CascadeResult {
  sourceNodeId: string;
  sourceNodeName: string;
  affectedNodes: { nodeId: string; nodeName: string; impact: number }[];
  totalImpact: number;
  maxDepth: number;
  paths: CascadePathStep[][];
}

/** Composite risk — cross-module combined risk for a region. */
export interface CompositeRisk {
  regionId: string;
  regionName: string;
  score: number;        // 0-100
  components: {
    convergence: number; // 0-100, weight 30%
    cii: number;         // 0-100, weight 50%
    infrastructure: number; // 0-100, weight 20%
  };
  boosts: {
    theaterPosture: number;  // 0-20 bonus
    breakingNews: number;    // 0-15 bonus
  };
  location: { lat: number; lng: number };
  detectedAt: string;
}

/** Aggregated intelligence signal — country-level signal summary. */
export interface AggregatedSignal {
  country: string;
  countryCode: string;
  signals: { type: string; count: number; severity: Severity }[];
  convergenceScore: number;
  zone: string | null; // Convergence zone label if detected
}

/** Velocity metrics — event throughput tracking. */
export interface VelocityMetrics {
  global: { rate: number; level: VelocityLevel; trend: VelocityTrend };
  perEntity: Map<string, { rate: number; level: VelocityLevel; trend: VelocityTrend }>;
  windowStart: string;
}

export type VelocityLevel = 'normal' | 'elevated' | 'spike';
export type VelocityTrend = 'rising' | 'stable' | 'falling';

/** Threat classification for a single event. */
export interface ThreatClassification {
  categories: { category: ThreatCategory; score: number }[];
  severity: Severity;
  confidence: number;
  tier: 'keyword' | 'compound' | 'ai';
}

export type ThreatCategory =
  | 'military' | 'terrorism' | 'cyber' | 'political' | 'economic'
  | 'environmental' | 'health' | 'infrastructure' | 'social' | 'diplomatic'
  | 'legal' | 'technology' | 'maritime' | 'aviation';

/** Extracted entity match from event text. */
export interface ExtractedEntityMatch {
  entityId: string;
  name: string;
  type: string;
  confidence: number;
  matchType: 'name' | 'alias' | 'keyword';
  source: 'title' | 'description' | 'related';
}

/** Trending keyword detection result. */
export interface TrendingKeyword {
  keyword: string;
  count: number;
  baseline: number;
  ratio: number;
  firstSeen: string;
}

/** Temporal baseline deviation for a source/type combination. */
export interface BaselineDeviation {
  source: string;
  type: string;
  currentValue: number;
  avg7d: number;
  avg30d: number;
  zScore: number;
  classification: 'normal' | 'elevated' | 'spike' | 'quiet';
}

// ---------------------------------------------------------------------------
// Phase D — Additional analysis module types (re-exported)
// ---------------------------------------------------------------------------

import type { BreakingNewsAlert } from './breaking-news';
import type { SentimentScore, CountrySentiment } from './sentiment';
import type { AISAnomaly } from './ais-spoofing';

export type { BreakingNewsAlert, SentimentScore, CountrySentiment, AISAnomaly };

/** Combined result from all analysis modules. */
export interface AnalysisResult {
  focalPoints: FocalPoint[];
  surges: SurgeEvent[];
  convergences: ConvergenceCell[];
  ciiScores: Map<string, CIIScore>;
  correlations: CorrelationSignal[];
  hotspotEscalations: HotspotEscalation[];
  cascades: CascadeResult[];
  compositeRisks: CompositeRisk[];
  aggregatedSignals: AggregatedSignal[];
  velocity: VelocityMetrics | null;
  trendingKeywords: TrendingKeyword[];
  breakingNews: BreakingNewsAlert[];
  sentimentScores: SentimentScore[];
  aisAnomalies: AISAnomaly[];
  timestamp: string;
}
